'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useMemo, useState } from 'react';
import { ConversionDialog } from './ConversionDialog';
import type { ConversionDialogProps } from './ConversionDialog/types';
import { ModalType } from './types';

type SelectedPlanWithFeature = Pick<
  ConversionDialogProps,
  | 'cost'
  | 'feature'
  | 'featureGateType'
  | 'id'
  | 'period'
  | 'shouldShowManageStorage'
  | 'shouldShowSelectedPlan'
  | 'shouldUpgradeSubscriptionImmediately'
  | 'storageVariant'
  | 'subscriptionId'
  | 'subscriptionUpdateSource'
  | 'tier'
  | 'title'
  | 'userLimit'
  | 'usageLimitExceeded'
  | 'isDiscountPlan'
  | 'isPausedPlan'
  | 'pauseOfferDurationMonths'
> & {
  modalType?: ModalType;
};

export type PlanManagementModalContextValue =
  | {
      isShowing: boolean;
      selectedPlanWithFeature?: SelectedPlanWithFeature;
      setSelectedPlanWithFeature: (
        selectedPlanWithFeature?: SelectedPlanWithFeature
      ) => void;
    }
  | undefined;

export const PlanManagementModalContext =
  createContext<PlanManagementModalContextValue>(undefined);

const DEFAULT_TIER = 'PRO';

export function PlanManagementModalProvider({
  children,
}: PropsWithChildren<object>) {
  const [selectedPlanWithFeature, setSelectedPlanWithFeature] = useState<
    SelectedPlanWithFeature | undefined
  >();
  const [isShowing, setIsShowing] = useState(false);
  const [modalType, setModalType] = useState(ModalType.CHECKOUT);

  const contextValue = useMemo(() => {
    return {
      isShowing,
      selectedPlanWithFeature,
      setSelectedPlanWithFeature: (
        planWithFeature?: SelectedPlanWithFeature
      ) => {
        if (!planWithFeature) {
          setIsShowing(false);
          return;
        }
        setModalType(planWithFeature.modalType || ModalType.CHECKOUT);
        setSelectedPlanWithFeature({
          ...planWithFeature,
          tier: planWithFeature.tier ?? DEFAULT_TIER,
        });
        setIsShowing(true);
      },
    };
  }, [isShowing, selectedPlanWithFeature, setSelectedPlanWithFeature]);

  return (
    <PlanManagementModalContext.Provider value={contextValue}>
      {children}
      <ConversionDialog
        cost={selectedPlanWithFeature?.cost ?? 0}
        feature={selectedPlanWithFeature?.feature}
        featureGateType={selectedPlanWithFeature?.featureGateType}
        id={selectedPlanWithFeature?.id ?? ''}
        isDiscountPlan={selectedPlanWithFeature?.isDiscountPlan}
        isPausedPlan={selectedPlanWithFeature?.isPausedPlan}
        isShowing={isShowing}
        onExited={() => setSelectedPlanWithFeature(undefined)}
        pauseOfferDurationMonths={
          selectedPlanWithFeature?.pauseOfferDurationMonths
        }
        period={selectedPlanWithFeature?.period ?? 'MONTHLY'}
        setIsShowing={setIsShowing}
        shouldShowManageStorage={modalType === ModalType.STORAGE}
        shouldShowSelectedPlan={modalType === ModalType.UPGRADE}
        shouldUpgradeSubscriptionImmediately={
          selectedPlanWithFeature?.shouldUpgradeSubscriptionImmediately
        }
        storageVariant={selectedPlanWithFeature?.storageVariant ?? 'active'}
        subscriptionUpdateSource={
          selectedPlanWithFeature?.subscriptionUpdateSource
        }
        tier={selectedPlanWithFeature?.tier}
        title={selectedPlanWithFeature?.title ?? ''}
        userLimit={selectedPlanWithFeature?.userLimit ?? 0}
      />
    </PlanManagementModalContext.Provider>
  );
}
